import React, { useEffect, useState, useRef, Fragment } from 'react'
import { useSelector } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'
import {
  Input,
  CardBody,
  Button,
  Form,
  FormGroup,
  Card, CardHeader, CardTitle, Col, Row,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  UncontrolledTooltip
} from 'reactstrap'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExpandAlt, faCompressAlt } from "@fortawesome/free-solid-svg-icons"
import { FilterIcon } from '@src/assets/icons'
import classNames from 'classnames'
import _ from 'lodash'
//************************************//
import '@styles/react/libs/react-select/_react-select.scss'
import CardAction from '@components/card-actions'
//************************************//
import { API, trans, selectThemeColors, fetchKeysFromQueryString, toLocalDateFormat, toBoolean } from '@utils'
//************************************//
import { DropdownTree, groupOptionFormatterCallback } from '../dropdown-tree'
import { DateRange } from "../date-rage"
import { InputGroupBuilder } from '../inputs/input-group-builder'
import InputContainer from "../helpers/InputContainer"
import { asyncSlelectLoadOptions } from "./Utils"
import QueryTabs from './QueryTabs'
import QueryModal from './QueryModal'
import FilterSearchControls from './FilterSearchControls'
import AsyncSelectWithActions from '../inputs/AsyncSelectWithActions'
import AdvancedFilters from "../inputs/advanced-filters"
import FilterFormColumn from './FilterFormColumn'
import { textOperators, numberOperators, selectOperators } from '@src/assets/data/constants'
//************************************//
const FilterForm = ({
  filterFormData,
  forcedFilter,
  hiddenFilter,
  defaultFilterMode,
  savedQueries,
  columns,
  onFilterReaction,
  onFilter,
  onSwitch,
  onQueryChange,
  onQuerySaveChange,
  onQuerySelect,
  onQueryDelete,
  lastVisit,
  disabled
}) => {
  const loading = useSelector(state => state.app.loading)
  const { handleSubmit, control, reset, setValue } = useForm()
  const [filterMode, setFilterMode] = useState(defaultFilterMode)
  const [showCard, setShowCard] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [dynamicFieldsVisibility, setDynamicFieldsVisibility] = useState({})
  const [saveFilters, setSaveFilters] = useState(false)
  const [saveAsyncFilters, setSaveAsyncFilters] = useState(false)
  const [loadOnce, setLoadOnce] = useState({})
  const [loadFilters, setLoadFilters] = useState(false)
  const refDropdown = useRef(null)
  const queryModalRef = useRef(null)
  const isCard = filterMode === 'card'
  const isDropdown = filterMode === 'dropdown'
  const isDropdownOpen = refDropdown?.current?.context?.isOpen || false
  //************************************//
  const onSelectFilterChange = (column, options) => {
    return new Promise((resolveDynamicFieldsVisibility) => {
      if (column.filter.onChange) column.filter.onChange(options, { dynamicFieldsVisibilityState: [dynamicFieldsVisibility, setDynamicFieldsVisibility], resolveDynamicFieldsVisibility })
    })
  }
  const _resetForm = () => {
    const defaultValues = {}
    _.map(columns, (c, k) => {
      if (c.filter && c.filter.enabled) {
        switch (c.filter.type) {
          case 'select':
          case 'asyncSelect': {
            defaultValues[c.selector] = []
            if (_.isNil(forcedFilter[c.selector])) {
              onSelectFilterChange(c, []).then(({ dynamicFieldsVisibility }) => {
                onFilterReaction({ dynamicFieldsVisibility })
              })
            }
          }; break
          case 'asyncDropdownTree': {
            defaultValues[c.selector] = []
          }; break
          case 'advancedFilters': {
            defaultValues[c.selector] = []
          }; break
          case 'dateRange': {
            defaultValues[c.selector] = []
          }; break
          case 'textBuilder':
          case 'numberBuilder':
          case 'selectBuilder': {
            defaultValues[c.selector] = []
          }; break
          case 'switch': {
            defaultValues[c.selector] = 'OFF'
          }; break
          default: {
            defaultValues[c.selector] = ''
          }; break
        }
      }
    })
    reset(defaultValues)
  }
  //************************************//
  const _reset = () => {
    _resetForm()
    if (_.size(forcedFilter) > 0) {
      onFilter({ ...forcedFilter })
      setSaveFilters((saveFilters) => ({
        filters: _.pickBy(saveFilters.filters, (f, k) => {
          return _.includes(_.keys(forcedFilter), k)
        }),
        data: _.pickBy(saveFilters.data, (f, k) => {
          return _.includes(_.keys(forcedFilter), k)
        })
      }))

      setSaveAsyncFilters((saveAsyncFilters) => {
        return _.pickBy(saveAsyncFilters, (f, k) => {
          return _.includes(_.keys(forcedFilter), k)
        })
      })
    } else {
      onFilter({})

      setSaveFilters(false)
      setSaveAsyncFilters(false)
    }
    if (isDropdown) setShowDropdown(false)
  }
  //************************************//
  const _resetDropdownFilterTag = (key) => {
    const defaultValues = {}
    _.map(columns, (c, k) => {
      if (c.filter && c.filter.enabled && c.selector === key) {
        switch (c.filter.type) {
          case 'select':
          case 'asyncSelect': {
            defaultValues[c.selector] = null
            onSelectFilterChange(c, null).then(({ dynamicFieldsVisibility }) => {
              onFilterReaction({ dynamicFieldsVisibility })
            })
          }; break
          case 'asyncDropdownTree': {
            defaultValues[c.selector] = null
          }; break
          case 'advancedFilters': {
            defaultValues[c.selector] = null
          }; break
          case 'dateRange': {
            defaultValues[c.selector] = []
          }; break
          case 'textBuilder':
          case 'numberBuilder':
          case 'selectBuilder': {
            defaultValues[c.selector] = []
          }; break
          case 'switch': {
            defaultValues[c.selector] = 'OFF'
          }; break
          default: {
            if (_.includes(c.selector, '.')) {
              const selectors = _.split(c.selector, '.')
              defaultValues[selectors[0]] = { ...defaultValues[selectors[0]], [selectors[1]]: '' }
            } else {
              defaultValues[c.selector] = ''
            }
          }; break
        }
      }
    })
    reset(defaultValues)
  }
  //************************************//
  const _clearDropdownFilterTag = (key) => {
    _resetDropdownFilterTag(key)
    const filters = {}, data = {}
    _.map(saveFilters.data, (o, k) => {
      const checkDeep = _.includes(key, '.')
      const selectors = _.split(key, '.')

      if (checkDeep && k === selectors[0]) {
        if (_.get(saveFilters.data, key) !== undefined) {
          data[k] = _.pickBy(o, function (v, k) {
            return k !== selectors[1]
          })
          if (_.size(data[k]) === 0) {
            delete data[k]
          }
        }
      } else {
        if (k !== key && o !== undefined) data[k] = o
      }
    })
    _.map(saveFilters.filters, (o, k) => {
      const checkDeep = _.includes(key, '.')
      const selectors = _.split(key, '.')

      if (checkDeep && k === selectors[0]) {
        if (_.get(saveFilters.filters, key) !== undefined) {
          filters[k] = _.pickBy(o, function (v, k) {
            return k !== selectors[1]
          })
          if (_.size(filters[k]) === 0) {
            delete filters[k]
          }
        }
      } else {
        if (k !== key && o !== undefined) filters[k] = o
      }
    })

    onFilter(filters)

    if (Object.keys(filters).length !== 0) {
      setSaveFilters({
        filters,
        data
      })
    } else {
      setSaveFilters(false)
      setSaveAsyncFilters(false)
    }
  }
  //************************************//
  const prepareFilters = (data) => {
    const filters = {}
    _.map(data, (v, k) => {
      if (_.isArray(v)) {
        let values = _.filter(v, i => {
          if (_.isObject(i) && _.has(i, 'operator')) {
            const value = _.isArray(i.value) ? i.value : [i.value]
            const emptyValues = _.filter(value, v => v === '')
            if (_.size(emptyValues) > 0) {
              return false
            }
          }
          return true
        })
        values = _.map(values, i => {
          if (_.isObject(i) && _.has(i, 'operator')) {
            return i
          }
          if (_.isObject(i)) {
            return i.value
          }
          return i
        })
        if (!_.isEmpty(values)) {
          filters[k] = values
        }
      } else if (_.isObject(v) && _.size(v) > 0) {
        if (!_.isUndefined(v.query) && v.query) {
          const { options_labels, selected_options, ...rest } = v
          if (_.size(rest.dynamic_filter) > 0) {
            filters[k] = rest
          } 
        } else if (!_.isUndefined(v.value) && v.value !== '') {
          filters[k] = v.value
        } else {
          _.forEach(v, (deepValue, deepKey) => {
            if (!_.isEmpty(deepValue)) {
              filters[k] = { ...filters[k], [deepKey]: !_.isUndefined(deepValue.value) ? deepValue.value : deepValue }
            }
          })
        }
      } else if (!_.isEmpty(v)) {
        if (_.includes(['OFF'], v)) {
          return
        }
        filters[k] = v
      }
    })
    return filters
  }
  //************************************//
  const onSubmit = data => {
    const filters = prepareFilters(data)
    onFilter(filters, { dynamicFieldsVisibility })

    if (Object.keys(filters).length !== 0) {
      setSaveFilters({
        filters,
        data
      })
    } else {
      setSaveFilters(false)
    }
    setLoadFilters(true)
    if (isDropdown) setShowDropdown(false)
  }
  //************************************//
  const _onQueryChange = ({ key, name }, callback) => {
    if (isDropdown) {
      setLoadOnce({})
    }
    if (onQueryChange) {
      onQueryChange({ key, name }, callback)
    }
  }
  //************************************//
  const _onQuerySaveChange = (query) => {
    if (onQuerySaveChange) {
      onQuerySaveChange(query)
    }
  }
  //************************************//
  const _onQuerySelect = (key) => {
    if (isDropdown) {
      setLoadOnce({})
    }
    if (onQuerySelect) {
      onQuerySelect(key)
    }
  }
  //************************************//
  const _onQueryDelete = (query) => {
    if (onQueryDelete) {
      onQueryDelete(query)
    }
  }
  //************************************//
  const _renderControl = (c, args) => {
    const { type, options = [], isMulti = false, components = null } = c.filter
    if (type === 'select') {
      return (
        <Controller
          render={(renderProps) => {
            return (
              <InputContainer
                // menuIsOpen
                isClearable
                name={renderProps.name}
                value={renderProps.value}
                isMulti={isMulti}
                options={options}
                className='react-select'
                // classNamePrefix='select'
                // theme={selectThemeColors}
                placeholder={trans('gen.reactSelect.placeholder')}
                type={'uncontrolled-select'}
                // menuPosition={'fixed'}
                noOptionsMessage={() => trans('gen.reactSelect.noOptions')}
                isColored={c.filter.isColored}
                components={components}
                styles={{
                  menu: base => ({
                    ...base,
                    zIndex: '99 !important'
                  })
                }}
                onChange={(options) => {
                  // colomn change filter
                  if (c.filter.onChange) onSelectFilterChange(c, options)
                  // form on change
                  renderProps.onChange(options)
                }}
              />
            )
          }}
          control={control}
          name={c.selector}
          defaultValue={null}
        />
      )
    } else if (type === 'asyncSelect') {
      const loadOptions = async (q, extraRemoteParams) => {
        return await c.filter.remoteUrl ? asyncSlelectLoadOptions(c.filter.remoteUrl, { q, ...extraRemoteParams }, c.filter.remoteUrlFormatter) : c.filter.loadOptions(q, extraRemoteParams)
      }
      return (
        <Controller
          render={(renderProps) => {
            return (
              <AsyncSelectWithActions
                isClearable
                name={renderProps.name}
                value={renderProps.value}
                isMulti={isMulti}
                className='react-select'
                // classNamePrefix='select'
                // theme={selectThemeColors}
                defaultOptions
                cacheOptions
                loadOptions={loadOptions}
                extraRemoteParams={c.filter.extraRemoteParams || false}
                optionFormatter={c.filter.optionFormatter}
                placeholder={trans('gen.reactSelect.placeholder')}
                components={c.filter.components || null}
                styles={{
                  menu: base => ({
                    ...base,
                    zIndex: '99 !important'
                  })
                }}
                onChange={(options) => {
                  // colomn change filter
                  if (c.filter.onChange) onSelectFilterChange(c, options)
                  // form on change
                  renderProps.onChange(options)
                }}
              />
            )
          }}
          control={control}
          name={c.selector}
          defaultValue={null}
        />
      )
    } else if (type === 'advancedFilters') {
      return (
        <Controller
          render={(renderProps) => {
            return (
              <AdvancedFilters
                label={c.name}
                name={renderProps.name}
                value={renderProps.value}
                remoteUrl={c.filter.remoteUrl}
                remoteUrlFormatter={c.filter.remoteUrlFormatter}
                optionFormatter={c.filter.optionFormatter}
                extraRemoteParams={c.filter.extraRemoteParams}
                placeholder={trans('gen.reactSelect.placeholder')}
                styles={{
                  menu: base => ({
                    ...base,
                    zIndex: '99 !important'
                  })
                }}
                onChange={(value) => {
                  // form on change
                  renderProps.onChange(value)
                }}
                onSwitch={(isAdvanced) => {
                  if (args.setIsFluid) args.setIsFluid(isAdvanced)
                }}
                advanced={{
                  ...c.filter.advanced,
                  modalPortal: document.getElementById(`dataTable-dropdown-menu-filter`)
                }}
              />
            )
          }}
          control={control}
          name={c.selector}
          defaultValue={null}
        />
      )
    } else if (type === 'asyncDropdownTree') {
      const { group = false, remoteUrl, loadOptions, optionFormatter, groupOptionFormat, clearable = false, dropdownOptions } = c.filter
      return (
        <DropdownTree
          name={c.selector}
          control={control}
          defaultValue={null}
          valueType={"arrayObject"}
          outputType={"arrayObject"}
          multi={isMulti}
          group={group}
          clearable={clearable}
          remoteUrl={remoteUrl}
          loadOptions={loadOptions}
          optionFormatter={optionFormatter}
          groupOptionFormat={groupOptionFormat}
          dropdownOptions={dropdownOptions}
        />
      )
    } else if (type === 'dateRange') {
      return (
        <DateRange
          name={c.selector}
          control={control}
          defaultValue={[]}
          ranges={c.filter.ranges}
          format={c.filter.format}
          displayFormat={c.filter.displayFormat}
          placeholder={c.filter.placeholder}
          parentEl={'.dataTable-dropdown-menu-card-filter'}
          showCustomRangeLabel={isCard && c.filter.showCustomRangeLabel}
          allowClear={true}
        />
      )
    } else if (type === 'switch') {
      return (
        <Controller
          render={(renderProps) => {
            return (
              <InputContainer
                type='uncontrolled-switch'
                id={c.selector}
                name={c.selector}
                onChange={(e) => {
                  renderProps.onChange(e.target.checked ? 'ON' : 'OFF')
                }}
                checked={renderProps.value === 'ON'}
                className={'zindex-0'}
              />
            )
          }}
          control={control}
          name={c.selector}
          defaultValue={'OFF'}
        />
      )
    } else if (type === 'number') {
      return (
        <Controller
          as={Input}
          type={'number'}
          control={control}
          name={c.selector}
          defaultValue=''
        />
      )
    } else if (type === 'textBuilder') {
      return (
        <InputGroupBuilder
          name={c.selector}
          type={'text'}
          control={control}
          defaultValue={[]}
        />
      )
    } else if (type === 'numberBuilder') {
      return (
        <InputGroupBuilder
          name={c.selector}
          type={'number'}
          control={control}
          defaultValue={[]}
        />
      )
    } else if (type === 'selectBuilder') {
      return (
        <InputGroupBuilder
          name={c.selector}
          type={'select'}
          control={control}
          options={options}
          isMulti={isMulti}
          defaultValue={[]}
        />
      )
    } else if (type === 'text') {
      return (
        <Controller
          as={Input}
          control={control}
          name={c.selector}
          defaultValue=''
        />
      )
    }
    return null
  }
  //************************************//
  const _renderItemCol = (c, k) => {
    if (c.filter && c.filter.enabled) {
      const forced = _.includes(_.keys(forcedFilter), c.selector)
      const hidden = _.includes(hiddenFilter, c.selector)
      if (c?.dynamicFieldVisibilityKey) {
        const dynamicFieldsVisibilityKeys = dynamicFieldsVisibility[c?.dynamicFieldVisibilityKey]
        if (dynamicFieldsVisibilityKeys === false) return
        if (_.size(dynamicFieldsVisibilityKeys) === 0 && !c?.dynamicFieldVisibleByDefault) return
        if (_.size(dynamicFieldsVisibilityKeys) > 0) {
          const colKey = c?.key ?? c?.selector
          const isDynamicFieldsVisible = _.includes(dynamicFieldsVisibilityKeys, colKey)
          if (!isDynamicFieldsVisible) {
            return
          }
        }
      }
      let breakPoints = _.get(c.filter, 'breakPoints', { md: 4, sm: 6 })
      if (isDropdown) {
        breakPoints = { xs: 12 }
      }
      return (
        <FilterFormColumn
          key={k}
          column={c}
          forced={forced}
          hidden={hidden}
          breakPoints={breakPoints}
          render={_renderControl}
        />
      )
    }
    return null
  }
  //************************************//
  const _renderInnerFilterTagValue = (col, value) => {
    const allowFilterValue = !_.includes(['switch'], col.filter.type)
    return allowFilterValue ? (
      <Fragment>
        <span className='mr-25'>:</span>
        <span>{value}</span>
      </Fragment>
    ) : ''
  }
  //************************************//
  const _getSelectedOperatorByValue = (_value) => {
    const operators = _.uniqBy([...textOperators, ...numberOperators, ...selectOperators], (i) => i.code)
    return _.find(operators, _operator => {
      const { operator, value, not } = _.head(_value)
      if (value === 'null' && not?.toString() === 'true') {
        return _operator.value === operator && _operator.not.toString() === not.toString() && _operator.null === true
      } else if (value === 'null') {
        return _operator.value === operator && _operator.null === true
      } else if (not?.toString() === "true") {
        return _operator.value === operator && _operator.not.toString() === not.toString()
      } else {
        return _operator.value === operator
      }
    })
  }
  //************************************//
  const _renderInnerFilterTag = (col, title, value) => {
    if (_.isEmpty(value) || _.isNil(value)) return
    if (typeof value === 'string') {
      return (
        <Fragment>
          <span>{title}</span>
          {_renderInnerFilterTagValue(col, value)}
        </Fragment>
      )
    }
    if (_.isArray(value)) {
      if (_.includes(['textBuilder', 'numberBuilder', 'selectBuilder'], col.filter.type)) {
        const checkEmptyValue = _.size(_.filter(value, (item, k) => {
          return _.isArray(item.value)
            ? _.size(_.filter(item.value, v => _.isEmpty(v))) === 0
            : !_.isEmpty(item.value)
        })) === 0
        if (checkEmptyValue) return
        const data =
          col.filter.type === 'selectBuilder'
            ? _.map(value, (item, k) => (item.value !== 'null' ? _.filter(col.filter.options, o => _.includes(item.value, o.value.toString())).map((o) => o.label) : '')).join(',')
            : _.map(value, (item, k) => (item.value !== 'null' ? item.value : '')).join(',')
        const selectedOperator = _getSelectedOperatorByValue(value)
        return (
          <Fragment>
            <span>{title}</span>
            {_renderInnerFilterTagValue(col, `${trans(selectedOperator?.label)} ${data}`)}
          </Fragment>
        )
      } else {
        const data = _.map(value, (item, k) => {
          const _item = typeof item === 'object' ? item.label : item
          if (col.filter.type === 'dateRange') {
            return toLocalDateFormat(_item, 'L', true)
          }
          return _item
        }).join(col.filter.type === 'dateRange' ? ' - ' : ', ')

        return (
          <Fragment>
            <span>{title}</span>
            {_renderInnerFilterTagValue(col, data)}
          </Fragment>
        )
      }
    }
    if (_.isObject(value) && toBoolean(value?.query)) {
      const tooltipValue = _.map(value.dynamic_filter, (item, key) => {
        const selectedOperator = _getSelectedOperatorByValue([item])
        const getTitle = () => {
          const label = _.get(value.selected_options, `${item.key}.label`)
          return trans(label)
        }
        const getValue = () => {
          return (
            <Fragment>
              {_.size(value.options_labels[item.key]) === 0 && <span>{_.isArray(item.value) ? _.join(item.value, ', ') : item.value}</span>}
              {_.size(value.options_labels[item.key]) > 0 && _.map(value.options_labels[item.key], (item) => item.label).join(', ')}
            </Fragment>
          )
        }
        return (
          <div key={key} className='text-nowrap'>
            <span>{getTitle()}</span>
            <strong className='mx-25'>{trans(selectedOperator.label)}</strong>
            {item.value !== 'null' && getValue()}
          </div>
        )
      })
      return (
        <Fragment>
          <div id={`${col.selector}_filter_tag_tooltip`}>
            <span>{title}</span>
            {_renderInnerFilterTagValue(col, `${_.map(value.dynamic_filter, item => {
              const label = value?.selected_options[item.key]?.label
              return trans(label)
            }).join(', ')}...`)}
          </div>
          <UncontrolledTooltip placement={'bottom'} target={`${col.selector}_filter_tag_tooltip`} className={'react-dataTable-filters-form-filter-tag-tooltip'}>
            <div className='text-left'>
              {tooltipValue}
            </div>
          </UncontrolledTooltip>
        </Fragment>
      )
    }
    if (_.isObject(value)) {
      return (
        <Fragment>
          <span>{title}</span>
          {_renderInnerFilterTagValue(col, value?.label)}
        </Fragment>
      )
    }
  }
  //************************************//
  const _renderButtonsDropdown = () => {
    return (
      <FormGroup row={true} className="p-2 mb-0">
        <Col>
          <Button color='flat-secondary' outline block disabled={loading} onClick={_reset}>
            <span>{trans('gen.actions.reset')}</span>
          </Button>
        </Col>
        <Col>
          <Button type='submit' color='primary' block disabled={loading}>
            <span>{trans('gen.actions.apply')}</span>
          </Button>
        </Col>
      </FormGroup>
    )
  }
  //************************************//
  const _renderButtonsCard = () => {
    return (
      <FormGroup className="p-2 mb-0">
        <Button type='submit' color='primary' disabled={loading} className="mr-1 dataTable-filter-card-button">
          <span>{trans('gen.actions.apply')}</span>
        </Button>
        <Button color='flat-secondary' disabled={loading} onClick={_reset} className="dataTable-filter-card-button">
          <span>{trans('gen.actions.reset')}</span>
        </Button>
      </FormGroup>
    )
  }
  //************************************//
  const _renderForm = () => {
    return (
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className='dataTable-filter-scroll p-2'>
          <Row>
            {_.map(columns, _renderItemCol)}
          </Row>
        </div>
        {isDropdown && _renderButtonsDropdown()}
        {isCard && _renderButtonsCard()}
      </Form>
    )
  }
  //************************************//
  const switchToDropdownRender = () => {
    const onClick = () => {
      setFilterMode('dropdown')
      setShowCard(false)
      setShowDropdown(true)
      onSwitch('dropdown')
    }
    return (
      <Button size={'sm'} color='light' className={'p-25 px-50 ml-50'} onClick={onClick}>
        <FontAwesomeIcon icon={faCompressAlt} className='m-0' />
      </Button>
    )
  }
  const _renderQueriesArea = () => {
    if (!savedQueries.enabled) return
    return (
      <div className='dataTable-queries my-1 border-bottom'>
        <QueryTabs savedQueries={savedQueries} onQueryAdd={() => queryModalRef.current.open()} onQueryRename={(data) => queryModalRef.current.open({ data, isEdit: true })} onQuerySaveChange={_onQuerySaveChange} onQuerySelect={_onQuerySelect} onQueryDelete={_onQueryDelete} />
      </div>
    )
  }
  //************************************//
  const _renderCardFilters = () => {
    return showCard ? (
      <div>
        {_renderQueriesArea()}
        <div className="dataTable-card-filter">
          <CardAction
            title={(
              <div>
                {trans('gen.datatable.filterOptions')}
                {switchToDropdownRender()}
              </div>
            )} actions='collapse'
          >
            <CardBody className='p-0 border-top'>
              {_renderForm()}
            </CardBody>
          </CardAction>
        </div>
      </div>
    ) : (
      <div className="d-flex align-items-center justify-content-between my-2">
        {_renderQueriesArea()}
        <Button color='primary' outline size="sm" onClick={() => setShowCard(true)} className="d-flex align-items-center" disabled={disabled}>
          <FontAwesomeIcon icon={FilterIcon} />
          <span className="ml-50">{trans('gen.datatable.filter')}</span>
        </Button>
      </div>
    )
  }
  const switchToCardRender = () => {
    const onClick = () => {
      setFilterMode('card')
      setShowCard(true)
      setShowDropdown(false)
      onSwitch('card')
    }
    return (
      <Button size={'sm'} color='light' className={'p-25 px-50 ml-50'} onClick={onClick}>
        <FontAwesomeIcon icon={faExpandAlt} className='m-0' />
      </Button>
    )
  }
  //************************************//
  const _renderDropdownFilters = () => {
    const filterColumns = _.filter(columns, (column) => column?.filter?.enabled)
    const data = _.map(filterColumns, (col, index) => {
      const key = col.selector
      const value = _.get(saveFilters.data, col.selector)
      const title = _renderInnerFilterTag(col, col.name, value)
      const _checkIsRendered = (value) => {
        if (_.includes(['switch'], col.filter.type)) {
          return _.includes(['ON'], value)
        }
        return value
      }
      const isRendered = saveFilters && _checkIsRendered(value)
      const hidden = _.includes(hiddenFilter, col.selector)
      const forced = _.includes(_.keys(forcedFilter), col.selector)

      return {
        key,
        title,
        hidden,
        forced,
        isRendered
      }
    })
    const showClearAll = _.size(
      _.pickBy(saveFilters.filters, (f, k) => {
        return !_.includes(_.keys(forcedFilter), k)
      })
    ) > 0
    return (
      <div>
        {_renderQueriesArea()}
        <div className="dataTable-dropdown-filter d-flex justify-content-between align-items-start my-2">
          <div className='dataTable-dropdown-filter-tags'>
            <FilterSearchControls data={data} showClearAll={showClearAll} onClearAll={_reset} onClear={(key) => _clearDropdownFilterTag(key)} />
          </div>
          <div className='d-flex'>
            <Dropdown isOpen={showDropdown} toggle={() => setShowDropdown(!showDropdown)} className={'ml-50'} direction='down' size='sm'>
              <DropdownToggle color='primary' outline ref={refDropdown} className="d-flex align-items-center" disabled={disabled}>
                <FontAwesomeIcon icon={FilterIcon} />
                <span className="ml-50">{trans('gen.datatable.filter')}</span>
              </DropdownToggle>
              <DropdownMenu id='dataTable-dropdown-menu-filter' className="dataTable-dropdown-menu-filter" right flip={false}>
                <Card className="dataTable-dropdown-menu-card-filter mb-0" title={trans('gen.actions.search')}>
                  <CardHeader className="p-1">
                    <CardTitle className={'d-flex align-items-center'}>
                      <h5 className="m-0">{trans('gen.datatable.filterOptions')}</h5>
                      {switchToCardRender()}
                    </CardTitle>
                  </CardHeader>
                  <CardBody className='p-0 border-top'>
                    {showDropdown && _renderForm()}
                  </CardBody>
                </Card>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      </div>
    )
  }
  //************************************//
  const getFormFilterValue = ({ obj = {}, column: c }) => {
    let value = null
    if (c?.filter?.enabled) {
      value = _.get(obj, c.selector)
      if (c?.filter?.type === 'select') {
        const filter = _.filter(c?.filter?.options, option => {
          if (c.filter.isMulti) {
            const _value = _.map(value, (v, i) => _.toString(v))
            return _.includes(_value, _.toString(option.value))
          } else {
            return _.toString(option.value) === _.toString(value)
          }
        })
        value = filter.length > 0 ? filter : null
      }
      if (c?.filter?.type === 'switch') {
        value = _.includes(['OFF', 'ON'], value) ? value : null
      }
    }
    return value
  }
  //************************************//
  const invokeAsyncDropdownTreeApi = ({ column, value, callback }) => {
    const asyncDropdownTreeCallback = ({ data: resData }) => {
      let value = resData
      if (column.filter.optionFormatter) {
        value = _.map(resData, option => column.filter.optionFormatter(option))
      }
      if (column.filter.groupOptionFormat) {
        value = groupOptionFormatterCallback(resData, column.filter.groupOptionFormat)
      }
      if (resData.length > 0) {
        callback(column, value)
      }
    }
    if (column.filter.remoteUrl) {
      API.get(column.filter.remoteUrl, {
        params: {
          ids: value
        }
      }).then(asyncDropdownTreeCallback)
    }
    if (column.filter.loadOptions) {
      column.filter.loadOptions({ ids: value }).then(asyncDropdownTreeCallback)
    }
  }
  //************************************//
  const invokeAsyncSelectApi = ({ column, value, callback }) => {
    const asyncSelectCallback = (resValue) => {
      if (resValue.length > 0) {
        const _value = _.map(value, (v, i) => _.toString(v))
        const resData = _.filter(resValue, item => _.includes(_value, _.toString(item.value)))
        callback(column, resData)
      }
    }
    const remoteUrl = column.filter.remoteUrl
    const extraRemoteParams = _.mapValues(_.keyBy(column.filter.extraRemoteParams, 'key'), () => true)
    if (remoteUrl) {
      asyncSlelectLoadOptions(remoteUrl, {
        q: '',
        ids: value,
        ...extraRemoteParams
      }, column.filter.remoteUrlFormatter).then(asyncSelectCallback)
    }
    if (column.filter.loadOptions) {
      const q = ''
      column.filter.loadOptions(q, value, extraRemoteParams).then(asyncSelectCallback)
    }
  }
  //************************************//
  const prepareAsyncCallback = (column, value) => {
    // check label if empty then set value instead
    const checkedValue = _.map(value, (v) => {
      const value = { ...v }
      value.label = value.label || value.value
      return value
    })
    // for tag and onFilter callback and fill form on open dropdown
    setSaveFilters(saveFilters => {
      const data = { ...saveFilters.data, [column.selector]: checkedValue }
      const filters = prepareFilters(data)
      return { ...saveFilters, data, filters }
    })
    // selected data
    setSaveAsyncFilters((saveAsyncFilters) => ({
      ...saveAsyncFilters,
      [column.selector]: checkedValue
    }))
    // onchange form
    if (column.filter.type === 'asyncSelect' && column.filter.onChange) {
      onSelectFilterChange(column, checkedValue).then(({ dynamicFieldsVisibility }) => {
        onFilterReaction({ dynamicFieldsVisibility })
      })
    }
    // fill form
    setValue(column.selector, checkedValue)
  }
  const prepareSimpleAdavancedFilterCallback = (column, options) => {
    setSaveFilters(saveFilters => {
      const data = { ...saveFilters.data, [column.selector]: options }
      const filters = prepareFilters(data)
      return { ...saveFilters, data, filters }
    })
  }
  //************************************//
  const prepareFormData = (obj, load) => {
    if (_.size(obj) > 0) {
      _.map(columns, (c) => {
        if (c.filter && c?.filter?.enabled) {
          const value = getFormFilterValue({ obj, column: c })
          if (value) {
            if (c.filter.type === 'asyncDropdownTree') {
              if (_.size(value) > 0) {
                if (load) {
                  invokeAsyncDropdownTreeApi({ column: c, value, callback: prepareAsyncCallback })
                } else {
                  setValue(c.selector, saveAsyncFilters[c.selector])
                }
              }
            } else if (c.filter.type === 'asyncSelect') {
              const prepareValue = c.filter.isMulti ? value : value ? [value] : []
              if (_.size(prepareValue) > 0) {
                if (load) {
                  invokeAsyncSelectApi({ column: c, value: prepareValue, callback: prepareAsyncCallback })
                } else {
                  setValue(c.selector, saveAsyncFilters[c.selector])
                }
              }
            } else if (c.filter.type === 'advancedFilters') {
              if (isDropdown && load && !_.get(loadOnce, c.selector, false)) {
                const {query, dynamic_filter} = value
                if (toBoolean(query) && _.size(dynamic_filter) > 0) {
                  const advancedFilters = _.keyBy(c.filter.advanced.filters, 'value')
                  const advancedFiltersRelations = _.reduce(c.filter.advanced.filters, (acc, filter) => {
                    return filter.relations && _.size(filter.relations) > 0
                      ? _.reduce(filter.relations, (obj, val, idx) => {
                          obj[val.key] = val
                          return obj
                        }, {})
                      : acc
                  }, false)
                  const dynamcFiltersKeys = _.map(dynamic_filter, (item) => item.key)
                  const dynamcFiltersValues = _.map(dynamic_filter, (item) => item.value)
                  const callApis = _.reduce(dynamcFiltersKeys, (acc, key, idx) => {
                    // relation remoteUrl
                    if (_.includes(key, '->')) {
                      const relationKey = _.head(_.split(key, '->'))
                      if (_.get(advancedFiltersRelations[relationKey], 'remoteUrl', '')) {
                        acc[relationKey] = {
                          api: API.get(advancedFiltersRelations[relationKey].remoteUrl),
                          formatter: _.get(advancedFiltersRelations[relationKey], 'remoteUrlFormatter', (item) => item)
                        }
                        return acc
                      }
                      return acc
                    }
                    // field remoteUrl
                    if (_.get(advancedFilters[key], 'remoteUrl', '')) {
                      acc[key] = {
                        api: API.get(_.get(advancedFilters[key], 'remoteUrl', ''), {
                          params: {
                            ids: dynamcFiltersValues[idx]
                          }
                        }),
                        formatter: _.get(advancedFilters[key], 'remoteUrlFormatter', (item) => item)
                      }
                      return acc
                    }
                    return acc
                  }, {})
                  const callApisKeys = _.keys(callApis)
                  const callApisUrls = _.map(callApis, callApi => callApi.api)
                  const apiUrlsCallback = (res) => {
                    const callApiResponses = _.reduce(callApisKeys, (acc, key, idx) => {
                      const data = _.get(res[idx], 'data', [])
                      const formattedData = _.map(data, item => callApis[key].formatter(item))
                      const formattedDataKeyByValue = _.keyBy(formattedData, 'value')
                      return {...acc, [key]: formattedDataKeyByValue}
                    }, {})
                    const selectedOptions = _.reduce(dynamcFiltersKeys, (acc, key, idx) => {
                      if (_.includes(key, '->')) {
                        const relationKey = _.head(_.split(key, '->'))
                        return {...acc, [key]: callApiResponses[relationKey][key]}
                      }
                      const option = advancedFilters[key]
                      return {...acc, [key]: option}
                    }, {})
                    const optionLabels = _.reduce(dynamcFiltersKeys, (acc, key, idx) => {
                      const option = advancedFilters[key]
                      if (option && _.includes(['asyncSelect'], option.type)) {
                        return {...acc, [key]: callApiResponses[key]}
                      }
                      return acc
                    }, {})
                    setSaveFilters(saveFilters => {
                      const data = { ...saveFilters.data, [c.selector]: {...value, selected_options: selectedOptions, options_labels: optionLabels} }
                      const filters = prepareFilters(data)
                      return {...saveFilters, data, filters }
                    })
                  }
                  Promise.all(callApisUrls)
                    .then(apiUrlsCallback)
                }
                if (!toBoolean(query) && _.size(value) > 0) {
                  invokeAsyncSelectApi({ column: c, value, callback: (c, options) => prepareSimpleAdavancedFilterCallback(c, options) })
                }
                setLoadOnce((loadOnce) => ({...loadOnce, [c.selector]: true}))
              }
              if (value) setValue(c.selector, value)
            } else {
              if (load) {
                setSaveFilters(saveFilters => {
                  const data = { ...saveFilters.data, [c.selector]: value }
                  const filters = prepareFilters(data)
                  return { ...saveFilters, data, filters }
                })
              }
              // onchange form
              if (c.filter.type === 'select' && c.filter.onChange) {
                onSelectFilterChange(c, value).then(({ dynamicFieldsVisibility }) => {
                  onFilterReaction({ dynamicFieldsVisibility })
                })
              }
              // fill form
              if (_.includes(['textBuilder', 'numberBuilder', 'selectBuilder'], c.filter.type)) {
                if (value) setValue(c.selector, value)
              } else {
                if (value) setValue(c.selector, _.isArray(value) && _.size(value) === 1 ? _.head(value) : value)
              }
            }
          }
        }
      })
    }
  }
  //************************************//
  const initForm = () => {
    const obj = _.size(saveFilters.filters) > 0
      ? saveFilters.filters
      : filterFormData

    prepareFormData(obj, loadFilters)
    setLoadFilters(false)
  }
  //************************************//
  useEffect(() => {
    if (savedQueries?.data?.selected_key) {
      _resetForm()
      setSaveFilters(false)
      setSaveAsyncFilters(false)
    }
  }, [savedQueries?.data?.selected_key])
  //************************************//
  useEffect(() => {
    if (!lastVisit) {
      _resetForm()
      setSaveFilters(false)
      setSaveAsyncFilters(false)
    }
  }, [lastVisit])
  //************************************//
  useEffect(() => {
    prepareFormData(filterFormData, true)

    if (isCard) {
      if (_.size(filterFormData) > 0) {
        setShowCard(true)
      }
    }
  }, [filterFormData, filterMode])
  //************************************//
  useEffect(() => {
    if (isDropdown && showDropdown) initForm()
    if (isCard && showCard) initForm()
  }, [showDropdown, showCard, isDropdown, isCard])
  //************************************//
  return (
    <Fragment>
      {isCard && _renderCardFilters()}
      {isDropdown && _renderDropdownFilters()}
      {savedQueries.enabled && <QueryModal ref={queryModalRef} onChange={_onQueryChange} />}
    </Fragment>
  )
}

export default FilterForm
